import { z } from 'zod';

// src/constants/academic.ts
var CLASS_LEVELS = [
  "BABY",
  "MIDDLE",
  "TOP",
  "P1",
  "P2",
  "P3",
  "P4",
  "P5",
  "P6",
  "P7",
  "S1",
  "S2",
  "S3",
  "S4",
  "S5",
  "S6"
];
var CLASS_LEVEL_GROUPS = ["NURSERY", "PRIMARY", "O_LEVEL", "A_LEVEL"];
var CLASS_LEVEL_GROUP_LABELS = {
  NURSERY: "Nursery",
  PRIMARY: "Primary",
  O_LEVEL: "O-Level",
  A_LEVEL: "A-Level"
};
var LEVEL_GROUP = {
  BABY: "NURSERY",
  MIDDLE: "NURSERY",
  TOP: "NURSERY",
  P1: "PRIMARY",
  P2: "PRIMARY",
  P3: "PRIMARY",
  P4: "PRIMARY",
  P5: "PRIMARY",
  P6: "PRIMARY",
  P7: "PRIMARY",
  S1: "O_LEVEL",
  S2: "O_LEVEL",
  S3: "O_LEVEL",
  S4: "O_LEVEL",
  S5: "A_LEVEL",
  S6: "A_LEVEL"
};
var CLASS_LEVEL_LABELS = {
  BABY: "Baby Class",
  MIDDLE: "Middle Class",
  TOP: "Top Class",
  P1: "Primary 1",
  P2: "Primary 2",
  P3: "Primary 3",
  P4: "Primary 4",
  P5: "Primary 5",
  P6: "Primary 6",
  P7: "Primary 7",
  S1: "Senior 1",
  S2: "Senior 2",
  S3: "Senior 3",
  S4: "Senior 4",
  S5: "Senior 5",
  S6: "Senior 6"
};
var CANDIDATE_LEVELS = ["P7", "S4", "S6"];
var isCandidateLevel = (level) => CANDIDATE_LEVELS.includes(level);
var levelsInGroup = (group) => CLASS_LEVELS.filter((l) => LEVEL_GROUP[l] === group);
var levelOrder = (level) => CLASS_LEVELS.indexOf(level);
function nextLevel(level) {
  if (level === "P7" || level === "S4" || level === "S6") return null;
  const next = CLASS_LEVELS[levelOrder(level) + 1];
  return next ?? null;
}
var TERMS_PER_YEAR = 3;
var TERM_NUMBERS = [1, 2, 3];
var TERM_LABELS = {
  1: "Term I",
  2: "Term II",
  3: "Term III"
};
var RESIDENCES = ["DAY", "BOARDING"];
var GENDERS = ["MALE", "FEMALE"];
var SCHOOL_DAYS = [1, 2, 3, 4, 5, 6];
var DAY_LABELS = {
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday"
};

// src/constants/modules.ts
var MODULE_KEYS = ["FEES", "SMS", "TIMETABLE", "ACADEMICS", "PARENT_PORTAL", "IMPORTS"];
var MODULE_LABELS = {
  FEES: "Fees & billing",
  SMS: "SMS to parents",
  TIMETABLE: "Timetable builder",
  ACADEMICS: "Academics & report cards",
  PARENT_PORTAL: "Parent portal",
  IMPORTS: "Bulk import"
};
var ALL_MODULE_KEYS = MODULE_KEYS;

// src/constants/roles.ts
var ROLES = [
  "SUPER_ADMIN",
  "DIRECTOR",
  "SCHOOL_ADMIN",
  "BURSAR",
  "HEAD_TEACHER",
  "DIRECTOR_OF_STUDIES",
  "CLASS_TEACHER",
  "TEACHER",
  "PARENT",
  "STUDENT",
  "LIBRARIAN"
];
var ORG_LEVEL_ROLES = ["SUPER_ADMIN", "DIRECTOR"];
var FINANCIAL_ROLES = ["SUPER_ADMIN", "DIRECTOR", "SCHOOL_ADMIN", "BURSAR"];
var PORTALS = ["admin", "teach", "parent", "student"];
var ROLE_PORTAL = {
  SUPER_ADMIN: "admin",
  DIRECTOR: "admin",
  SCHOOL_ADMIN: "admin",
  BURSAR: "admin",
  HEAD_TEACHER: "admin",
  DIRECTOR_OF_STUDIES: "admin",
  LIBRARIAN: "admin",
  CLASS_TEACHER: "teach",
  TEACHER: "teach",
  PARENT: "parent",
  STUDENT: "student"
};
var PORTAL_PRECEDENCE = ["admin", "teach", "parent", "student"];
function portalsForRoles(roles) {
  const set = new Set(roles.map((r) => ROLE_PORTAL[r]));
  return PORTAL_PRECEDENCE.filter((p) => set.has(p));
}
function primaryPortal(roles) {
  return portalsForRoles(roles)[0] ?? null;
}
var isOrgLevelRole = (role) => ORG_LEVEL_ROLES.includes(role);
var requiresTwoFactor = (roles) => roles.some((r) => FINANCIAL_ROLES.includes(r));
var ROLE_LABELS = {
  SUPER_ADMIN: "Platform Admin",
  DIRECTOR: "Director",
  SCHOOL_ADMIN: "School Administrator",
  BURSAR: "Bursar",
  HEAD_TEACHER: "Head Teacher",
  DIRECTOR_OF_STUDIES: "Director of Studies",
  CLASS_TEACHER: "Class Teacher",
  TEACHER: "Teacher",
  PARENT: "Parent / Guardian",
  STUDENT: "Student",
  LIBRARIAN: "Librarian"
};

// src/domain/grading.ts
function lookupGrade(score, bands) {
  return bands.find((b) => score >= b.minScore && score <= b.maxScore) ?? null;
}
function validateGradeBands(bands) {
  if (bands.length === 0) return "Add at least one grade band";
  for (const b of bands) {
    if (b.minScore > b.maxScore) {
      return `"${b.grade}" has a minimum score (${b.minScore}) above its maximum (${b.maxScore})`;
    }
    if (b.minScore < 0 || b.maxScore > 100) {
      return `"${b.grade}" must fall within 0-100`;
    }
  }
  const sorted = [...bands].sort((a, b) => a.minScore - b.minScore);
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    if (curr.minScore <= prev.maxScore) {
      return `"${prev.grade}" (${prev.minScore}-${prev.maxScore}) overlaps "${curr.grade}" (${curr.minScore}-${curr.maxScore})`;
    }
  }
  return null;
}
function weightedAverage(entries) {
  if (entries.length === 0) return null;
  const totalWeight = entries.reduce((sum2, e) => sum2 + e.weight, 0);
  if (totalWeight <= 0) return null;
  const weightedSum = entries.reduce((sum2, e) => sum2 + e.score * e.weight, 0);
  return weightedSum / totalWeight;
}
function computeSubjectResult(entries, bands) {
  const average = weightedAverage(entries);
  if (average === null) return null;
  const band = lookupGrade(Math.round(average), bands);
  return {
    average,
    grade: band?.grade ?? null,
    points: band?.points ?? null,
    descriptor: band?.descriptor ?? null
  };
}
function validateDivisionBands(bands) {
  if (bands.length === 0) return "Add at least one division band";
  for (const b of bands) {
    if (b.minAggregate > b.maxAggregate) {
      return `Division "${b.division}" has a minimum aggregate above its maximum`;
    }
  }
  const sorted = [...bands].sort((a, b) => a.minAggregate - b.minAggregate);
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    if (curr.minAggregate <= prev.maxAggregate) {
      return `Division "${prev.division}" (${prev.minAggregate}-${prev.maxAggregate}) overlaps "${curr.division}" (${curr.minAggregate}-${curr.maxAggregate})`;
    }
  }
  return null;
}
function lookupDivision(aggregate, bands) {
  return bands.find((b) => aggregate >= b.minAggregate && aggregate <= b.maxAggregate)?.division ?? null;
}
function computeOverallResult(config, coreSubjectResults) {
  if (config.type === "NONE") return { aggregate: null, division: null };
  if (coreSubjectResults.length === 0 || coreSubjectResults.some((r) => r === null || r.points === null)) {
    return { aggregate: null, division: null };
  }
  const aggregate = coreSubjectResults.reduce((sum2, r) => sum2 + (r?.points ?? 0), 0);
  const division = lookupDivision(aggregate, config.divisionBands);
  return { aggregate, division };
}
function computePositions(students, direction = "DESC") {
  const sorted = [...students].sort((a, b) => direction === "DESC" ? b.value - a.value : a.value - b.value);
  const positions = /* @__PURE__ */ new Map();
  let lastValue = null;
  let lastPosition = 0;
  sorted.forEach((s, index) => {
    if (lastValue === null || s.value !== lastValue) {
      lastPosition = index + 1;
      lastValue = s.value;
    }
    positions.set(s.studentId, lastPosition);
  });
  return positions;
}
function computeTotalScore(subjectResults) {
  const withResults = subjectResults.filter((r) => r !== null);
  if (withResults.length === 0) return null;
  return withResults.reduce((sum2, r) => sum2 + r.average, 0);
}

// src/domain/import-headers.ts
var STUDENT_ALIASES = {
  firstName: ["first name", "firstname", "given name", "givenname", "fname", "forename"],
  middleName: ["middle name", "middlename", "other names", "othernames", "mname"],
  lastName: ["last name", "lastname", "surname", "family name", "sname"],
  // Many rolls carry one column with everything in it.
  fullName: [
    "name",
    "full name",
    "fullname",
    "names",
    "student name",
    "pupil name",
    "name of pupil",
    "name of student",
    "learner name"
  ],
  gender: ["gender", "sex", "m f", "mf"],
  dateOfBirth: ["date of birth", "dateofbirth", "dob", "d o b", "birth date", "birthdate"],
  classLevel: ["class", "level", "grade", "form", "class level", "current class"],
  stream: ["stream", "section", "arm", "class stream"],
  residence: ["residence", "day boarding", "day or boarding", "boarding status", "type", "category"],
  studentNumber: [
    "student number",
    "studentno",
    "student no",
    "admission number",
    "admission no",
    "adm no",
    "admno",
    "reg no",
    "registration number",
    "index number"
  ],
  lin: ["lin", "learner id", "learner identification number", "emis number", "emis no"],
  house: ["house", "dormitory", "dorm"],
  admittedOn: ["admitted on", "date admitted", "admission date", "date of admission", "joined"],
  guardianName: [
    "parent name",
    "parents name",
    "parent",
    "guardian",
    "guardian name",
    "name of parent",
    "name of guardian",
    "father name",
    "mother name",
    "next of kin"
  ],
  guardianPhone: [
    "phone",
    "telephone",
    "tel",
    "contact",
    "parent phone",
    "parents tel",
    "parent tel",
    "guardian phone",
    "guardian contact",
    "phone number",
    "contact number",
    "mobile"
  ],
  guardianRelationship: ["relationship", "relation", "parent type"],
  guardianEmail: ["email", "e mail", "parent email", "guardian email"],
  medicalNotes: ["medical", "medical notes", "allergies", "health notes", "conditions"]
};
function normalizeHeader(header) {
  return header.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim().replace(/\s+/g, " ");
}
var STUDENT_LOOKUP = (() => {
  const map = /* @__PURE__ */ new Map();
  for (const [field, aliases] of Object.entries(STUDENT_ALIASES)) {
    for (const alias of aliases) map.set(normalizeHeader(alias), field);
  }
  return map;
})();
var REQUIRED_STUDENT_FIELDS = ["classLevel"];
function hasUsableName(fields) {
  const present = new Set(fields);
  return present.has("fullName") || present.has("firstName") && present.has("lastName");
}
function mapStudentHeaders(headers) {
  const matched = /* @__PURE__ */ new Map();
  const unmatched = [];
  const taken = /* @__PURE__ */ new Set();
  headers.forEach((header, index) => {
    const field = STUDENT_LOOKUP.get(normalizeHeader(header));
    if (field && !taken.has(field)) {
      matched.set(index, field);
      taken.add(field);
    } else if (header.trim()) {
      unmatched.push({ index, header: header.trim() });
    }
  });
  const missing = REQUIRED_STUDENT_FIELDS.filter((f) => !taken.has(f));
  return { matched, unmatched, missing };
}
function splitFullName(full) {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0] ?? "", lastName: "" };
  const first = parts[0] ?? "";
  const isSurnameFirst = first.length > 1 && first === first.toUpperCase() && /\p{L}/u.test(first);
  if (isSurnameFirst) {
    const [surname = "", given = "", ...rest] = parts;
    return {
      lastName: titleCase(surname),
      firstName: titleCase(given),
      middleName: rest.length ? rest.map(titleCase).join(" ") : void 0
    };
  }
  const lastName = parts[parts.length - 1] ?? "";
  const middle = parts.slice(1, -1);
  return {
    firstName: titleCase(first),
    middleName: middle.length ? middle.map(titleCase).join(" ") : void 0,
    lastName: titleCase(lastName)
  };
}
function titleCase(word) {
  if (!word) return word;
  return word[0].toUpperCase() + word.slice(1).toLowerCase();
}

// src/domain/import-values.ts
var ok = (value) => ({ ok: true, value });
var fail = (message) => ({ ok: false, message });
function coerceGender(raw) {
  const v = String(raw ?? "").trim().toLowerCase();
  if (!v) return fail("Gender is missing");
  if (["m", "male", "boy", "b", "1"].includes(v)) return ok("MALE");
  if (["f", "female", "girl", "g", "2"].includes(v)) return ok("FEMALE");
  return fail(`"${String(raw)}" is not a gender, use M or F`);
}
function coerceResidence(raw) {
  const v = String(raw ?? "").trim().toLowerCase();
  if (!v) return ok("DAY");
  if (["d", "day", "day scholar", "dayscholar", "ds"].includes(v)) return ok("DAY");
  if (["b", "boarding", "boarder", "bd", "residential"].includes(v)) return ok("BOARDING");
  return fail(`"${String(raw)}" is not a residence, use Day or Boarding`);
}
function coerceClassLevel(raw) {
  const v = String(raw ?? "").trim().toLowerCase().replace(/[.\s_-]+/g, "");
  if (!v) return fail("Class is missing");
  if (["baby", "babyclass", "nursery1", "n1"].includes(v)) return ok("BABY");
  if (["middle", "middleclass", "nursery2", "n2"].includes(v)) return ok("MIDDLE");
  if (["top", "topclass", "nursery3", "n3", "prep"].includes(v)) return ok("TOP");
  const primary = /^(?:p|pri|primary)?(\d)$/.exec(v);
  if (primary) {
    const n = Number(primary[1]);
    if (n >= 1 && n <= 7) return ok(`P${n}`);
  }
  const secondary = /^(?:s|sen|senior|form)(\d)$/.exec(v);
  if (secondary) {
    const n = Number(secondary[1]);
    if (n >= 1 && n <= 6) return ok(`S${n}`);
  }
  const words = {
    seniorone: "S1",
    seniortwo: "S2",
    seniorthree: "S3",
    seniorfour: "S4",
    seniorfive: "S5",
    seniorsix: "S6"
  };
  const word = words[v];
  if (word) return ok(word);
  const exact = CLASS_LEVELS.find((l) => l.toLowerCase() === v);
  if (exact) return ok(exact);
  return fail(`"${String(raw)}" is not a class, use P1\u2013P7, S1\u2013S6, or Baby/Middle/Top`);
}
var EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
var MS_PER_DAY = 864e5;
function coerceDate(raw) {
  if (raw === null || raw === void 0 || String(raw).trim() === "") {
    return fail("Date is missing");
  }
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return ok(toIsoDate(raw));
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    if (raw < 1 || raw > 6e4) return fail(`${raw} is not a date`);
    return ok(toIsoDate(new Date(EXCEL_EPOCH_MS + Math.round(raw) * MS_PER_DAY)));
  }
  const text = String(raw).trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (iso) {
    const [, y, m, d] = iso;
    return buildDate(Number(y), Number(m), Number(d), text);
  }
  const parts = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/.exec(text);
  if (parts) {
    const day = Number(parts[1]);
    const month = Number(parts[2]);
    let year = Number(parts[3]);
    if (year < 100) year += year <= 30 ? 2e3 : 1900;
    if (month > 12 && day <= 12) {
      return fail(`"${text}" looks like month/day, dates must be day first, e.g. 05/03/2015`);
    }
    return buildDate(year, month, day, text);
  }
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) return ok(toIsoDate(parsed));
  return fail(`"${text}" is not a date, use dd/mm/yyyy`);
}
function buildDate(year, month, day, original) {
  if (month < 1 || month > 12) return fail(`"${original}" has no month ${month}`);
  if (day < 1 || day > 31) return fail(`"${original}" has no day ${day}`);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return fail(`"${original}" is not a real date`);
  }
  return ok(toIsoDate(date));
}
function toIsoDate(date) {
  return date.toISOString().slice(0, 10);
}
function coerceText(raw, { maxLength = 240 } = {}) {
  if (raw === null || raw === void 0) return ok(null);
  const text = String(raw).trim().replace(/\s+/g, " ");
  if (!text) return ok(null);
  if (text.length > maxLength) return fail(`Too long, keep it under ${maxLength} characters`);
  return ok(text);
}
var RELATIONSHIPS = ["FATHER", "MOTHER", "GUARDIAN", "SPONSOR", "SIBLING", "OTHER"];
function coerceRelationship(raw) {
  const v = String(raw ?? "").trim().toLowerCase();
  if (!v) return ok("GUARDIAN");
  if (["father", "dad", "f", "papa", "baba"].includes(v)) return ok("FATHER");
  if (["mother", "mum", "mom", "m", "mama", "maama"].includes(v)) return ok("MOTHER");
  if (["guardian", "g", "uncle", "aunt", "aunty", "grandmother", "grandfather"].includes(v)) {
    return ok("GUARDIAN");
  }
  if (["sponsor", "ngo", "benefactor"].includes(v)) return ok("SPONSOR");
  if (["brother", "sister", "sibling"].includes(v)) return ok("SIBLING");
  return ok("OTHER");
}

// src/domain/money.ts
var CURRENCIES = {
  UGX: { code: "UGX", decimals: 0, symbol: "UGX" },
  USD: { code: "USD", decimals: 2, symbol: "$" },
  KES: { code: "KES", decimals: 2, symbol: "KSh" },
  TZS: { code: "TZS", decimals: 0, symbol: "TSh" }
};
var DEFAULT_CURRENCY = "UGX";
var CurrencyMismatchError = class extends Error {
  constructor(a, b) {
    super(`Cannot combine ${a} with ${b} \u2014 convert explicitly first`);
    this.name = "CurrencyMismatchError";
  }
};
function money(amount, currency = DEFAULT_CURRENCY) {
  if (typeof amount === "number" && !Number.isInteger(amount)) {
    throw new TypeError(
      `Money amount must be an integer in minor units, got ${amount}. Use majorToMinor() to convert a decimal amount.`
    );
  }
  return { amount: BigInt(amount), currency };
}
var zero = (currency = DEFAULT_CURRENCY) => money(0n, currency);
function assertSameCurrency(a, b) {
  if (a.currency !== b.currency) throw new CurrencyMismatchError(a.currency, b.currency);
}
function add(a, b) {
  assertSameCurrency(a, b);
  return { amount: a.amount + b.amount, currency: a.currency };
}
function subtract(a, b) {
  assertSameCurrency(a, b);
  return { amount: a.amount - b.amount, currency: a.currency };
}
function sum(items, currency = DEFAULT_CURRENCY) {
  return items.reduce((acc, m) => add(acc, m), zero(currency));
}
function negate(m) {
  return { amount: -m.amount, currency: m.currency };
}
var isZero = (m) => m.amount === 0n;
var isPositive = (m) => m.amount > 0n;
var isNegative = (m) => m.amount < 0n;
function compare(a, b) {
  assertSameCurrency(a, b);
  if (a.amount < b.amount) return -1;
  if (a.amount > b.amount) return 1;
  return 0;
}
function percentage(m, percent) {
  if (!Number.isFinite(percent)) throw new TypeError(`Invalid percentage: ${percent}`);
  const scaled = m.amount * BigInt(Math.round(percent * 1e4));
  const divisor = 1000000n;
  const quotient = scaled / divisor;
  const remainder = scaled % divisor;
  const roundUp = remainder * 2n >= divisor;
  return { amount: roundUp ? quotient + 1n : quotient, currency: m.currency };
}
function allocate(m, parts) {
  if (!Number.isInteger(parts) || parts < 1) {
    throw new RangeError(`parts must be a positive integer, got ${parts}`);
  }
  const n = BigInt(parts);
  const base = m.amount / n;
  const remainder = m.amount % n;
  return Array.from({ length: parts }, (_, i) => ({
    amount: base + (BigInt(i) < remainder ? 1n : 0n),
    currency: m.currency
  }));
}
function majorToMinor(value, currency = DEFAULT_CURRENCY) {
  const { decimals } = CURRENCIES[currency];
  const raw = String(value).trim().replace(/,/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(raw)) {
    throw new TypeError(`Cannot parse "${value}" as a ${currency} amount`);
  }
  const negative = raw.startsWith("-");
  const [whole = "0", fraction = ""] = raw.replace(/^-/, "").split(".");
  const padded = (fraction + "0".repeat(decimals)).slice(0, decimals);
  const combined = BigInt(whole + padded);
  return negative ? -combined : combined;
}
function minorToMajor(amount, currency = DEFAULT_CURRENCY) {
  const { decimals } = CURRENCIES[currency];
  if (decimals === 0) return amount.toString();
  const negative = amount < 0n;
  const abs = negative ? -amount : amount;
  const divisor = 10n ** BigInt(decimals);
  const whole = abs / divisor;
  const fraction = (abs % divisor).toString().padStart(decimals, "0");
  return `${negative ? "-" : ""}${whole}.${fraction}`;
}
function formatMoney(m, opts = {}) {
  const { symbol = true } = opts;
  const { decimals, symbol: sym } = CURRENCIES[m.currency];
  const negative = m.amount < 0n;
  const abs = negative ? -m.amount : m.amount;
  const major = minorToMajor(abs, m.currency);
  const [whole = "0", fraction] = major.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const body = decimals > 0 && fraction ? `${grouped}.${fraction}` : grouped;
  return `${negative ? "-" : ""}${symbol ? `${sym} ` : ""}${body}`;
}

// src/domain/phone.ts
var UG_COUNTRY_CODE = "256";
var UG_NSN_LENGTH = 9;
var MOBILE_LEADING = "7";
var FIXED_LEADING = /* @__PURE__ */ new Set(["2", "3", "4"]);
var InvalidPhoneNumberError = class extends Error {
  constructor(input, reason) {
    super(`Invalid Uganda phone number "${input}": ${reason}`);
    this.input = input;
    this.reason = reason;
    this.name = "InvalidPhoneNumberError";
  }
  input;
  reason;
};
function scrub(input) {
  const trimmed = input.trim();
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  return hasPlus ? `+${digits}` : digits;
}
function toNationalNumber(input) {
  let s = scrub(input);
  if (s.startsWith("+")) s = s.slice(1);
  if (s.startsWith(UG_COUNTRY_CODE)) {
    s = s.slice(UG_COUNTRY_CODE.length);
    if (s.startsWith("0")) s = s.slice(1);
    return s.length === UG_NSN_LENGTH ? s : null;
  }
  if (s.length === UG_NSN_LENGTH + 1 && s.startsWith("0")) {
    return s.slice(1);
  }
  if (s.length === UG_NSN_LENGTH) return s;
  return null;
}
function isValidUgPhone(input) {
  const nsn = toNationalNumber(input);
  if (!nsn) return false;
  const lead = nsn[0];
  if (!lead) return false;
  return lead === MOBILE_LEADING || FIXED_LEADING.has(lead);
}
function normalizeUgPhone(input) {
  const nsn = toNationalNumber(input);
  if (!nsn) {
    throw new InvalidPhoneNumberError(
      input,
      `expected ${UG_NSN_LENGTH} digits after the country code`
    );
  }
  const lead = nsn[0];
  if (!lead || lead !== MOBILE_LEADING && !FIXED_LEADING.has(lead)) {
    throw new InvalidPhoneNumberError(
      input,
      `national numbers start with 7 (mobile) or 2, 3, 4 (fixed), got "${lead ?? ""}"`
    );
  }
  return `+${UG_COUNTRY_CODE}${nsn}`;
}
function tryNormalizeUgPhone(input) {
  try {
    return normalizeUgPhone(input);
  } catch {
    return null;
  }
}
function phoneKind(input) {
  const nsn = toNationalNumber(input);
  const lead = nsn?.[0];
  if (!lead) return null;
  if (lead === MOBILE_LEADING) return "mobile";
  if (FIXED_LEADING.has(lead)) return "fixed";
  return null;
}
function canReceiveSms(input) {
  return phoneKind(input) === "mobile";
}
function formatUgPhone(input) {
  const nsn = toNationalNumber(input);
  if (!nsn) return input;
  return `+${UG_COUNTRY_CODE} ${nsn.slice(0, 3)} ${nsn.slice(3, 6)} ${nsn.slice(6)}`;
}

// src/domain/sms.ts
var GSM7_BASIC = new Set(
  `@\xA3$\xA5\xE8\xE9\xF9\xEC\xF2\xC7
\xD8\xF8\r\xC5\xE5\u0394_\u03A6\u0393\u039B\u03A9\u03A0\u03A8\u03A3\u0398\u039E\xC6\xE6\xDF\xC9 !"#\xA4%&'()*+,-./0123456789:;<=>?\xA1ABCDEFGHIJKLMNOPQRSTUVWXYZ\xC4\xD6\xD1\xDC\xA7\xBFabcdefghijklmnopqrstuvwxyz\xE4\xF6\xF1\xFC\xE0`
);
var GSM7_EXTENDED = /* @__PURE__ */ new Set(["^", "{", "}", "\\", "[", "~", "]", "|", "\u20AC", "\f"]);
var SEGMENT_LIMITS = {
  /** Single-part message. */
  gsm7: 160,
  /** Concatenated part — 7 septets go to the user data header. */
  gsm7Concat: 153,
  /** 140 bytes / 2 bytes per character. */
  ucs2: 70,
  /** 134 bytes / 2 after the header. */
  ucs2Concat: 67
};
function isGsm7(text) {
  for (const char of text) {
    if (!GSM7_BASIC.has(char) && !GSM7_EXTENDED.has(char)) return false;
  }
  return true;
}
function findNonGsm7(text) {
  const found = [];
  const seen = /* @__PURE__ */ new Set();
  for (const char of text) {
    if (GSM7_BASIC.has(char) || GSM7_EXTENDED.has(char)) continue;
    if (seen.has(char)) continue;
    seen.add(char);
    found.push(char);
  }
  return found;
}
function gsm7Septets(text) {
  let septets = 0;
  for (const char of text) {
    septets += GSM7_EXTENDED.has(char) ? 2 : 1;
  }
  return septets;
}
function calculateSmsCost(text) {
  const offending = findNonGsm7(text);
  if (offending.length === 0) {
    const length2 = gsm7Septets(text);
    const perPart2 = length2 <= SEGMENT_LIMITS.gsm7 ? SEGMENT_LIMITS.gsm7 : SEGMENT_LIMITS.gsm7Concat;
    const segments2 = length2 === 0 ? 1 : Math.ceil(length2 / perPart2);
    return {
      encoding: "GSM-7",
      length: length2,
      segments: segments2,
      remainingInSegment: segments2 * perPart2 - length2,
      offendingCharacters: []
    };
  }
  const length = text.length;
  const perPart = length <= SEGMENT_LIMITS.ucs2 ? SEGMENT_LIMITS.ucs2 : SEGMENT_LIMITS.ucs2Concat;
  const segments = length === 0 ? 1 : Math.ceil(length / perPart);
  return {
    encoding: "UCS-2",
    length,
    segments,
    remainingInSegment: segments * perPart - length,
    offendingCharacters: offending
  };
}
var SUBSTITUTIONS = [
  [/[‘’‚′]/g, "'"],
  // curly single quotes, prime
  [/[“”„″]/g, '"'],
  // curly double quotes
  [/[–—―]/g, "-"],
  // en dash, em dash, horizontal bar
  [/…/g, "..."],
  // ellipsis
  // Escaped, not literal. An invisible character in source is unreadable and
  // easy to delete by accident, and catching invisible characters is the point.
  [/[\u00A0\u2007\u202F]/g, " "],
  // no-break, figure, narrow no-break space
  [/[•·]/g, "-"],
  // bullets
  [/™/g, "(TM)"],
  [/®/g, "(R)"],
  [/©/g, "(C)"],
  [/[→⇒]/g, "->"]
];
function sanitizeToGsm7(text) {
  return SUBSTITUTIONS.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), text);
}
function campaignSegments(body, recipientCount) {
  return calculateSmsCost(body).segments * recipientCount;
}

// src/domain/sms-campaign.ts
var PLACEHOLDER_PATTERN = /\{\{(\w+)\}\}/g;
function extractPlaceholders(body) {
  const found = /* @__PURE__ */ new Set();
  for (const match of body.matchAll(PLACEHOLDER_PATTERN)) {
    found.add(match[1]);
  }
  return [...found];
}
function renderTemplate(body, context) {
  const missing = [];
  const text = body.replace(PLACEHOLDER_PATTERN, (match, key) => {
    if (Object.hasOwn(context, key)) return context[key];
    missing.push(key);
    return match;
  });
  return { text, missing: [...new Set(missing)] };
}
function canRender(body, context) {
  return extractPlaceholders(body).every((p) => Object.hasOwn(context, p));
}
function resolveRecipients(raw, template) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const r of raw) {
    const { text } = renderTemplate(template, r.context);
    const key = `${r.phone}\0${text}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      phone: r.phone,
      guardianId: r.guardianId ?? null,
      studentId: r.studentId ?? null,
      renderedBody: text
    });
  }
  return out;
}
function previewCampaignCost(recipients) {
  let totalSegments = 0;
  let hasUcs2Recipient = false;
  for (const r of recipients) {
    const cost = calculateSmsCost(r.renderedBody);
    totalSegments += cost.segments;
    if (cost.encoding === "UCS-2") hasUcs2Recipient = true;
  }
  return { recipientCount: recipients.length, totalSegments, hasUcs2Recipient };
}
var EAT_OFFSET_MS = 3 * 60 * 60 * 1e3;
var QUIET_HOURS_START_EAT = 7;
var QUIET_HOURS_END_EAT = 20;
function isWithinQuietHours(date) {
  const eatHour = new Date(date.getTime() + EAT_OFFSET_MS).getUTCHours();
  return eatHour >= QUIET_HOURS_START_EAT && eatHour < QUIET_HOURS_END_EAT;
}
function nextQuietHoursWindow(date) {
  if (isWithinQuietHours(date)) return date;
  const shifted = new Date(date.getTime() + EAT_OFFSET_MS);
  const eatHour = shifted.getUTCHours();
  const target = new Date(shifted);
  target.setUTCMinutes(0, 0, 0);
  target.setUTCHours(QUIET_HOURS_START_EAT);
  if (eatHour >= QUIET_HOURS_END_EAT) {
    target.setUTCDate(target.getUTCDate() + 1);
  }
  return new Date(target.getTime() - EAT_OFFSET_MS);
}

// src/domain/student-number.ts
var DEFAULT_NUMBER_FORMAT = "{SCHOOL}/{YEAR}/{SEQ}";
var DEFAULT_SEQ_WIDTH = 4;
function formatIdentifier({
  format = DEFAULT_NUMBER_FORMAT,
  shortCode,
  year,
  sequence
}) {
  const code = (shortCode ?? "").trim().toUpperCase();
  return format.replace(/\{SCHOOL\}/g, code).replace(/\{YEAR\}/g, year).replace(/\{YY\}/g, year.slice(-2)).replace(
    /\{SEQ:(\d+)\}/g,
    (_m, width) => String(sequence).padStart(Number(width), "0")
  ).replace(/\{SEQ\}/g, String(sequence).padStart(DEFAULT_SEQ_WIDTH, "0")).replace(/^[/\s-]+/, "").replace(/([/-])\1+/g, "$1");
}
function deriveShortCode(schoolName) {
  const words = schoolName.replace(/[^\p{L}\s]/gu, " ").split(/\s+/).filter(Boolean).filter((w) => !["of", "the", "and", "for"].includes(w.toLowerCase()));
  const initials = words.map((w) => w[0]?.toUpperCase() ?? "").join("");
  return initials.slice(0, 4) || "SCH";
}
function validateNumberFormat(format) {
  if (!format.includes("{SEQ}") && !/\{SEQ:\d+\}/.test(format)) {
    return "The format must include {SEQ}, or every student would get the same number";
  }
  if (format.length > 60) return "That format is too long";
  if (/\{(?!SCHOOL|YEAR|YY|SEQ(:\d+)?)[A-Z:]+\}/.test(format)) {
    return "Unknown token. Use {SCHOOL}, {YEAR}, {YY} or {SEQ}";
  }
  return null;
}

// src/domain/timetable.ts
function findHardConflicts(candidate, existingSlots) {
  const conflicts = [];
  const others = existingSlots.filter((s) => s.id !== candidate.excludeSlotId);
  const sameSlotTime = (s) => s.dayOfWeek === candidate.dayOfWeek && s.periodId === candidate.periodId;
  if (others.some((s) => sameSlotTime(s) && s.streamId === candidate.streamId)) {
    conflicts.push({
      severity: "HARD",
      code: "STREAM_BUSY",
      message: "This class already has a subject in this period."
    });
  }
  if (others.some((s) => sameSlotTime(s) && s.staffId === candidate.staffId)) {
    conflicts.push({
      severity: "HARD",
      code: "TEACHER_BUSY",
      message: "This teacher is already teaching another class in this period."
    });
  }
  if (candidate.roomId && others.some((s) => sameSlotTime(s) && s.roomId === candidate.roomId)) {
    conflicts.push({
      severity: "HARD",
      code: "ROOM_BUSY",
      message: "This room is already booked in this period."
    });
  }
  return conflicts;
}
function hasHardConflict(conflicts) {
  return conflicts.some((c) => c.severity === "HARD");
}
function weeklyPeriodCount(staffId, slots) {
  return slots.filter((s) => s.staffId === staffId).length;
}
function checkTeacherLoad(staffId, slots, maxPeriodsPerWeek) {
  if (maxPeriodsPerWeek === null) return null;
  const count = weeklyPeriodCount(staffId, slots);
  if (count <= maxPeriodsPerWeek) return null;
  return {
    severity: "WARN",
    code: "TEACHER_OVERLOADED",
    message: `This teacher now has ${count} periods this week, above their cap of ${maxPeriodsPerWeek}.`
  };
}
function checkSubjectQuota(subjectId, streamId, slots, weeklyTarget) {
  if (weeklyTarget === null) return null;
  const count = slots.filter((s) => s.subjectId === subjectId && s.streamId === streamId).length;
  if (count >= weeklyTarget) return null;
  return {
    severity: "WARN",
    code: "SUBJECT_UNDER_QUOTA",
    message: `This subject has ${count} of its ${weeklyTarget} weekly periods placed for this class.`
  };
}
function checkTeacherQualified(staffId, subjectId, qualifiedPairs) {
  if (qualifiedPairs.has(qualificationKey(staffId, subjectId))) return null;
  return {
    severity: "WARN",
    code: "TEACHER_UNQUALIFIED",
    message: "This teacher is not marked as qualified to teach this subject."
  };
}
function qualificationKey(staffId, subjectId) {
  return `${staffId}:${subjectId}`;
}
function findFreeStaff(dayOfWeek, periodId, allStaffIds, slots, qualification) {
  const busy = new Set(
    slots.filter((s) => s.dayOfWeek === dayOfWeek && s.periodId === periodId).map((s) => s.staffId)
  );
  return allStaffIds.filter((id) => {
    if (busy.has(id)) return false;
    if (!qualification) return true;
    return qualification.qualifiedPairs.has(qualificationKey(id, qualification.subjectId));
  });
}
var uuidSchema = z.string().uuid("Must be a valid ID");
var phoneSchema = z.string().trim().min(1, "Phone number is required").refine((v) => tryNormalizeUgPhone(v) !== null, {
  message: "Enter a Ugandan phone number, e.g. 0771 234 567"
}).transform(normalizeUgPhone);
var emailSchema = z.string().trim().toLowerCase().email("Enter a valid email address");
var paginationSchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25)
});
var moneyStringSchema = z.string().regex(/^-?\d+(\.\d{1,2})?$/, "Enter an amount, e.g. 250000");
var dateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use the format YYYY-MM-DD").refine((v) => !Number.isNaN(Date.parse(v)), { message: "Not a real date" });

// src/schemas/people.ts
var name = (label) => z.string().trim().min(1, `${label} is required`).max(80, `${label} is too long`).regex(/^[\p{L}][\p{L}'\-. ]*$/u, `${label} contains characters that are not part of a name`);
var classLevelSchema = z.enum(CLASS_LEVELS);
var classLevelGroupSchema = z.enum(CLASS_LEVEL_GROUPS);
var genderSchema = z.enum(GENDERS);
var residenceSchema = z.enum(RESIDENCES);
var relationshipSchema = z.enum([
  "FATHER",
  "MOTHER",
  "GUARDIAN",
  "SPONSOR",
  "SIBLING",
  "OTHER"
]);
var guardianInputSchema = z.object({
  firstName: name("First name"),
  lastName: name("Last name"),
  phone: phoneSchema,
  altPhone: phoneSchema.optional(),
  email: emailSchema.optional(),
  nin: z.string().trim().max(30).optional(),
  occupation: z.string().trim().max(120).optional(),
  address: z.string().trim().max(240).optional()
});
var linkGuardianSchema = z.object({
  guardianId: uuidSchema.optional(),
  /** Supply either an existing guardian's id, or the details to create one. */
  guardian: guardianInputSchema.optional(),
  relationship: relationshipSchema,
  isPrimary: z.boolean().default(false),
  receivesSms: z.boolean().default(true),
  receivesInvoices: z.boolean().default(true),
  isEmergencyContact: z.boolean().default(false),
  mayCollect: z.boolean().default(true)
}).refine((d) => Boolean(d.guardianId) !== Boolean(d.guardian), {
  message: "Provide either an existing guardian or new guardian details, not both",
  path: ["guardianId"]
});
var createStudentSchema = z.object({
  firstName: name("First name"),
  middleName: name("Middle name").optional(),
  lastName: name("Last name"),
  dateOfBirth: dateOnlySchema.optional(),
  gender: genderSchema,
  residence: residenceSchema.default("DAY"),
  house: z.string().trim().max(40).optional(),
  /** Left blank to have one generated from the school's format. */
  studentNumber: z.string().trim().max(40).optional(),
  lin: z.string().trim().max(30).optional(),
  unebIndexNo: z.string().trim().max(30).optional(),
  admittedOn: dateOnlySchema,
  medicalNotes: z.string().trim().max(2e3).optional(),
  /** Enrol immediately. Omitted only when back-filling historical records. */
  streamId: uuidSchema.optional(),
  termId: uuidSchema.optional(),
  guardians: z.array(linkGuardianSchema).max(6).default([])
});
var updateStudentSchema = createStudentSchema.omit({ guardians: true, streamId: true, termId: true }).partial();
var studentQuerySchema = z.object({
  /** Matches across first, middle, last name and student number. */
  search: z.string().trim().max(80).optional(),
  streamId: uuidSchema.optional(),
  level: classLevelSchema.optional(),
  status: z.enum(["ACTIVE", "TRANSFERRED_OUT", "GRADUATED", "DROPPED_OUT", "SUSPENDED"]).optional(),
  residence: residenceSchema.optional(),
  termId: uuidSchema.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25)
});
var enrolStudentSchema = z.object({
  studentId: uuidSchema,
  termId: uuidSchema,
  streamId: uuidSchema,
  residence: residenceSchema.optional(),
  enrolledOn: dateOnlySchema.optional()
});
var transferStreamSchema = z.object({
  studentId: uuidSchema,
  termId: uuidSchema,
  streamId: uuidSchema,
  reason: z.string().trim().max(240).optional()
});
var promoteSchema = z.object({
  fromTermId: uuidSchema,
  toTermId: uuidSchema,
  /** Streams to promote. Empty means every stream in the school. */
  streamIds: z.array(uuidSchema).default([]),
  /** Students who stay in their current class. */
  repeatStudentIds: z.array(uuidSchema).default([]),
  /** Students leaving the school — P7, S4 and S6 finishers, and transfers out. */
  exitStudentIds: z.array(uuidSchema).default([]),
  /** Explicit level -> stream choice, since a level may have several streams. */
  streamMapping: z.record(uuidSchema, uuidSchema).default({}),
  dryRun: z.boolean().default(true)
});
var exitStudentSchema = z.object({
  status: z.enum(["TRANSFERRED_OUT", "GRADUATED", "DROPPED_OUT"]),
  exitedOn: dateOnlySchema,
  reason: z.string().trim().max(240).optional()
});
var createStreamSchema = z.object({
  level: classLevelSchema,
  /** Blank where a level has a single stream — then it displays as just "P5". */
  name: z.string().trim().max(40).default(""),
  capacity: z.number().int().min(1).max(500).optional()
});
var createSubjectSchema = z.object({
  code: z.string().trim().min(1).max(20).toUpperCase(),
  name: z.string().trim().min(2).max(120),
  level: classLevelGroupSchema,
  isCore: z.boolean().default(false)
});
var createStaffSchema = z.object({
  firstName: name("First name"),
  lastName: name("Last name"),
  phone: phoneSchema,
  email: emailSchema.optional(),
  gender: genderSchema.optional(),
  dateOfBirth: dateOnlySchema.optional(),
  nin: z.string().trim().max(30).optional(),
  staffNumber: z.string().trim().max(40).optional(),
  type: z.enum(["TEACHING", "NON_TEACHING", "SUPPORT"]),
  employmentType: z.enum(["PERMANENT", "CONTRACT", "PART_TIME"]),
  dateJoined: dateOnlySchema,
  maxPeriodsPerWeek: z.number().int().min(1).max(60).optional(),
  /** Subjects this person is qualified to teach — not what they teach now. */
  subjectIds: z.array(uuidSchema).default([]),
  qualifications: z.array(
    z.object({
      title: z.string().trim().min(2).max(160),
      institution: z.string().trim().max(160).optional(),
      yearAwarded: z.number().int().min(1950).max(2100).optional()
    })
  ).default([]),
  /** Creates a login for this person, using their phone number. */
  createLogin: z.boolean().default(false),
  roles: z.array(z.enum(["TEACHER", "CLASS_TEACHER", "BURSAR", "HEAD_TEACHER", "DIRECTOR_OF_STUDIES", "SCHOOL_ADMIN", "LIBRARIAN"])).default([])
});
var staffAccessRoleSchema = z.enum([
  "DIRECTOR",
  "SCHOOL_ADMIN",
  "BURSAR",
  "HEAD_TEACHER",
  "DIRECTOR_OF_STUDIES",
  "CLASS_TEACHER",
  "TEACHER",
  "LIBRARIAN"
]);
var updateStaffAccessSchema = z.object({
  roles: z.array(staffAccessRoleSchema).max(8)
});
var updateStaffSchema = createStaffSchema.omit({ createLogin: true, roles: true, qualifications: true }).partial();
var assignStaffSchema = z.object({
  staffId: uuidSchema,
  termId: uuidSchema,
  streamId: uuidSchema,
  subjectId: uuidSchema.optional(),
  role: z.enum(["SUBJECT_TEACHER", "CLASS_TEACHER", "HOD"]).default("SUBJECT_TEACHER")
}).refine((d) => d.role === "CLASS_TEACHER" || Boolean(d.subjectId), {
  message: "A subject teacher needs a subject",
  path: ["subjectId"]
});

// src/schemas/academics.ts
var gradeBandInputSchema = z.object({
  minScore: z.number().int().min(0).max(100),
  maxScore: z.number().int().min(0).max(100),
  grade: z.string().trim().min(1, "Enter a grade, e.g. D1").max(10),
  points: z.number().int().min(0).max(20),
  descriptor: z.string().trim().max(80).optional()
}).refine((b) => b.minScore <= b.maxScore, {
  message: "The minimum score must not exceed the maximum",
  path: ["minScore"]
});
var divisionBandInputSchema = z.object({
  minAggregate: z.number().int().min(0),
  maxAggregate: z.number().int().min(0),
  division: z.string().trim().min(1, "Enter a division, e.g. I").max(10)
}).refine((b) => b.minAggregate <= b.maxAggregate, {
  message: "The minimum aggregate must not exceed the maximum",
  path: ["minAggregate"]
});
var aggregationConfigSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("NONE") }),
  z.object({
    type: z.literal("SUM_CORE_SUBJECTS_TO_DIVISION"),
    divisionBands: z.array(divisionBandInputSchema).min(1, "Add at least one division band"),
    rankDirection: z.enum(["ASC", "DESC"]).default("ASC")
  })
]);
var createGradingSchemeSchema = z.object({
  name: z.string().trim().min(2, "Give the scheme a name, e.g. PLE Standard").max(120),
  appliesTo: classLevelGroupSchema,
  aggregation: aggregationConfigSchema,
  bands: z.array(gradeBandInputSchema).min(1, "Add at least one grade band")
});
var assessmentKindSchema = z.enum(["BOT", "MOT", "EOT", "CA"]);
var createAssessmentSchema = z.object({
  termId: uuidSchema,
  kind: assessmentKindSchema,
  name: z.string().trim().min(2, "Enter a name, e.g. Mid-Term Exam").max(120),
  weight: z.number().int().min(1).max(100)
});
var markEntrySchema = z.object({
  studentId: uuidSchema,
  /** 0-100. Omit a student to leave their mark for this round unset. */
  score: z.number().int().min(0).max(100)
});
var saveMarksSchema = z.object({
  assessmentId: uuidSchema,
  subjectId: uuidSchema,
  streamId: uuidSchema,
  marks: z.array(markEntrySchema).min(1, "Enter at least one mark").refine((marks) => new Set(marks.map((m) => m.studentId)).size === marks.length, {
    message: "Each student can only appear once"
  })
});
var attendanceStatusSchema = z.enum(["PRESENT", "ABSENT", "LATE", "EXCUSED"]);
var attendanceEntrySchema = z.object({
  studentId: uuidSchema,
  status: attendanceStatusSchema,
  /** Expected when status is EXCUSED; not enforced here, since a bursar
   * entering the register mid-lesson should never be blocked on it. */
  reason: z.string().trim().max(240).optional()
});
var markAttendanceSchema = z.object({
  streamId: uuidSchema,
  termId: uuidSchema,
  date: dateOnlySchema,
  records: z.array(attendanceEntrySchema).min(1, "Mark at least one student").refine((records) => new Set(records.map((r) => r.studentId)).size === records.length, {
    message: "Each student can only appear once"
  })
});
var setReportCardCommentsSchema = z.object({
  classTeacherComment: z.string().trim().max(500).optional(),
  headTeacherComment: z.string().trim().max(500).optional()
});
var loginSchema = z.object({
  phone: phoneSchema.optional(),
  email: emailSchema.optional(),
  password: z.string().min(1, "Enter your password"),
  /** Required only once the account has TOTP enabled. */
  totpCode: z.string().regex(/^\d{6}$/, "Enter the 6-digit code").optional()
}).superRefine((value, ctx) => {
  if (!value.phone && !value.email) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["phone"], message: "Enter your phone or email" });
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["email"], message: "Enter your phone or email" });
  }
  if (value.phone && value.email) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["phone"], message: "Use one sign-in method" });
  }
});
var passwordSchema = z.string().min(8, "Use at least 8 characters").max(128, "That is too long");
var changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Enter your current password"),
  newPassword: passwordSchema,
  confirmPassword: z.string()
}).refine((d) => d.newPassword === d.confirmPassword, {
  message: "The two passwords do not match",
  path: ["confirmPassword"]
});
var redeemPasswordResetSchema = z.object({
  token: z.string().min(32, "That reset link looks incomplete"),
  newPassword: passwordSchema,
  confirmPassword: z.string()
}).refine((d) => d.newPassword === d.confirmPassword, {
  message: "The two passwords do not match",
  path: ["confirmPassword"]
});
var requestPasswordResetSchema = z.object({ phone: phoneSchema });
var confirmPasswordResetSchema = z.object({
  phone: phoneSchema,
  otp: z.string().regex(/^\d{6}$/, "Enter the 6-digit code you were sent"),
  newPassword: passwordSchema
});
var feeItemCategorySchema = z.enum([
  "TUITION",
  "DEVELOPMENT",
  "LUNCH",
  "BOARDING",
  "UNIFORM",
  "EXAMS",
  "PTA",
  "TRANSPORT",
  "OTHER"
]);
var studentCategorySchema = z.enum(["NEW", "CONTINUING"]);
var discountKindSchema = z.enum(["PERCENTAGE", "FIXED_AMOUNT"]);
var paymentMethodSchema = z.enum(["CASH", "BANK_DEPOSIT", "MTN_MOMO", "AIRTEL_MONEY", "CHEQUE"]);
var createFeeItemSchema = z.object({
  code: z.string().trim().min(1, "Enter a short code, e.g. TUITION").max(20).toUpperCase(),
  name: z.string().trim().min(2, "Enter a name, e.g. Tuition").max(120),
  category: feeItemCategorySchema
});
var feeStructureLineSchema = z.object({
  feeItemId: uuidSchema,
  /** Major units, e.g. "250000" or "250000.00" — converted to minor units server-side. */
  amount: moneyStringSchema
});
var createFeeStructureSchema = z.object({
  termId: uuidSchema,
  classLevel: classLevelSchema,
  residence: residenceSchema,
  studentCategory: studentCategorySchema.default("CONTINUING"),
  lines: z.array(feeStructureLineSchema).min(1, "Add at least one fee item").refine((lines) => new Set(lines.map((l) => l.feeItemId)).size === lines.length, {
    message: "Each fee item can only appear once in a fee structure"
  })
});
var createDiscountSchema = z.object({
  studentId: uuidSchema,
  name: z.string().trim().min(2, "Give the discount a name, e.g. Sibling discount").max(120),
  kind: discountKindSchema,
  /** 1-100. Required when kind is PERCENTAGE. */
  percentage: z.number().int().min(1).max(100).optional(),
  /** Major units. Required when kind is FIXED_AMOUNT. */
  fixedAmount: moneyStringSchema.optional(),
  /** Omit to apply across the whole invoice rather than one fee item. */
  feeItemId: uuidSchema.optional(),
  startTermId: uuidSchema,
  endTermId: uuidSchema.optional(),
  reason: z.string().trim().max(240).optional()
}).refine((d) => d.kind === "PERCENTAGE" ? d.percentage !== void 0 : d.fixedAmount !== void 0, {
  message: "Enter a percentage or a fixed amount to match the kind chosen",
  path: ["percentage"]
}).refine((d) => !d.endTermId || d.endTermId !== d.startTermId, {
  message: "A discount ending in its own starting term never applies to anything",
  path: ["endTermId"]
});
var generateInvoicesSchema = z.object({
  termId: uuidSchema,
  /** Empty means every active enrolment in the term. */
  streamIds: z.array(uuidSchema).default([]),
  dryRun: z.boolean().default(true)
});
var voidInvoiceSchema = z.object({
  reason: z.string().trim().min(3, "Say why this invoice is being voided").max(240)
});
var recordPaymentSchema = z.object({
  studentId: uuidSchema,
  termId: uuidSchema,
  method: paymentMethodSchema,
  /** Major units, e.g. "300000" for a partial payment. */
  amount: moneyStringSchema,
  paidAt: dateOnlySchema,
  reference: z.string().trim().max(120).optional(),
  notes: z.string().trim().max(240).optional(),
  /**
   * Client-generated, resubmitted unchanged on retry. Required, not
   * optional — a payment endpoint with no idempotency key is the one place in
   * this system where a retried request causes real financial harm.
   */
  idempotencyKey: z.string().trim().min(8).max(80)
});
var reversePaymentSchema = z.object({
  reason: z.string().trim().min(3, "Say why this payment is being reversed").max(240)
});
var createAdjustmentSchema = z.object({
  studentId: uuidSchema,
  termId: uuidSchema,
  direction: z.enum(["DEBIT", "CREDIT"]),
  /** Major units. */
  amount: moneyStringSchema,
  /** Required — an adjustment with no reason is exactly what an audit trail exists to catch. */
  reason: z.string().trim().min(3, "Say why this adjustment is being made").max(240)
});
var arrearsQuerySchema = z.object({
  termId: uuidSchema.optional(),
  classLevel: classLevelSchema.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25)
});
var slugSchema = z.string().trim().toLowerCase().min(3, "Use at least 3 characters").max(40, "Keep it under 40 characters").regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "Lowercase letters, numbers and hyphens only");
var createOrganizationSchema = z.object({
  name: z.string().trim().min(2, "Enter the organisation name").max(120),
  slug: slugSchema
});
var levelGroupsSchema = z.array(z.enum(CLASS_LEVEL_GROUPS)).min(1, "Select at least one level");
var createSchoolSchema = z.object({
  name: z.string().trim().min(2, "Enter the school name").max(160),
  slug: slugSchema,
  /** Ministry of Education centre number, where the school has one. */
  centreNumber: z.string().trim().max(20).optional(),
  phone: phoneSchema.optional(),
  email: emailSchema.optional(),
  district: z.string().trim().max(60).optional(),
  address: z.string().trim().max(240).optional(),
  motto: z.string().trim().max(160).optional(),
  levelGroups: levelGroupsSchema
});
var createTenantSchema = z.object({
  organization: createOrganizationSchema,
  school: createSchoolSchema
});
var updateOrganizationSchema = z.object({
  name: z.string().trim().min(2).max(120).optional()
});
var updateModulesSchema = z.object({
  enabledModules: z.array(z.enum(MODULE_KEYS))
});
var updatePlatformSettingsSchema = z.object({
  defaultEnabledModules: z.array(z.enum(MODULE_KEYS)).optional(),
  maintenanceMode: z.boolean().optional(),
  maintenanceMessage: z.string().trim().max(240).optional(),
  supportEmail: emailSchema.optional(),
  supportPhone: phoneSchema.optional()
});
var updateSchoolSchema = z.object({
  name: z.string().trim().min(2).max(160).optional(),
  centreNumber: z.string().trim().max(20).optional(),
  phone: phoneSchema.optional(),
  email: emailSchema.optional(),
  district: z.string().trim().max(60).optional(),
  address: z.string().trim().max(240).optional(),
  motto: z.string().trim().max(160).optional(),
  levelGroups: levelGroupsSchema.optional()
});
var createAcademicYearSchema = z.object({
  name: z.string().trim().regex(/^\d{4}$/, "Use a four-digit year, e.g. 2026"),
  startDate: dateOnlySchema,
  endDate: dateOnlySchema
}).refine((d) => d.startDate < d.endDate, {
  message: "The year must end after it starts",
  path: ["endDate"]
});
var createTermSchema = z.object({
  academicYearId: uuidSchema,
  number: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  /** Printed on every report card, so it is captured with the term itself. */
  nextTermBegins: dateOnlySchema.optional()
}).refine((d) => d.startDate < d.endDate, {
  message: "The term must end after it starts",
  path: ["endDate"]
}).refine((d) => !d.nextTermBegins || d.nextTermBegins > d.endDate, {
  message: "Next term cannot begin before this one ends",
  path: ["nextTermBegins"]
});
var TERM_NUMBER_VALUES = TERM_NUMBERS;
var messageCategorySchema = z.enum([
  "FEE_REMINDER",
  "PAYMENT_RECEIVED",
  "ABSENCE_ALERT",
  "RESULTS_PUBLISHED",
  "ANNOUNCEMENT",
  "CUSTOM"
]);
var messageBodySchema = z.string().trim().min(1, "Enter the message").max(1e3);
var createTemplateSchema = z.object({
  code: z.string().trim().min(1, "Enter a short code, e.g. FEE_REMIND").max(30).toUpperCase(),
  name: z.string().trim().min(2, "Enter a name").max(120),
  category: messageCategorySchema,
  body: messageBodySchema
});
var audienceSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("ALL_GUARDIANS") }),
  z.object({ type: z.literal("ALL_STAFF") }),
  z.object({ type: z.literal("GUARDIANS_OF_STREAMS"), streamIds: z.array(uuidSchema).min(1) }),
  z.object({ type: z.literal("STUDENTS"), studentIds: z.array(uuidSchema).min(1) }),
  z.object({ type: z.literal("CUSTOM_PHONES"), phones: z.array(phoneSchema).min(1) })
]);
var createCampaignSchema = z.object({
  name: z.string().trim().min(2, "Give the campaign a name").max(160),
  templateId: uuidSchema.optional(),
  /** Required when templateId is omitted — a one-off message. */
  body: messageBodySchema.optional(),
  audience: audienceSchema
}).refine((d) => Boolean(d.templateId) || Boolean(d.body), {
  message: "Choose a template or write a one-off message",
  path: ["body"]
});
var topUpCreditsSchema = z.object({
  segments: z.number().int().min(1, "Enter a positive number of segments"),
  reference: z.string().trim().max(120).optional(),
  reason: z.string().trim().max(240).optional()
});
var refundCreditsSchema = z.object({
  segments: z.number().int().min(1, "Enter a positive number of segments"),
  reason: z.string().trim().min(3, "Say why these credits are being refunded").max(240)
});
var timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use 24-hour HH:MM, e.g. 08:00");
var periodInputSchema = z.object({
  number: z.number().int().min(1),
  name: z.string().trim().min(1, "Enter a name, e.g. Period 1").max(40),
  startTime: timeOfDaySchema,
  endTime: timeOfDaySchema,
  isTeaching: z.boolean().default(true)
}).refine((p) => p.endTime > p.startTime, {
  message: "End time must be after start time",
  path: ["endTime"]
});
var createPeriodStructureSchema = z.object({
  name: z.string().trim().min(2, "Enter a name, e.g. Primary day").max(120),
  periods: z.array(periodInputSchema).min(1, "Add at least one period").refine((periods) => new Set(periods.map((p) => p.number)).size === periods.length, {
    message: "Each period number can only appear once"
  })
});
var createRoomSchema = z.object({
  name: z.string().trim().min(1, "Enter a name, e.g. Science Lab").max(80),
  capacity: z.number().int().min(1).optional()
});
var createTimetableVersionSchema = z.object({
  termId: uuidSchema,
  periodStructureId: uuidSchema,
  name: z.string().trim().min(2, "Enter a name, e.g. Term II 2026 draft").max(160)
});
var dayOfWeekSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6)
]);
var placeSlotSchema = z.object({
  versionId: uuidSchema,
  streamId: uuidSchema,
  dayOfWeek: dayOfWeekSchema,
  periodId: uuidSchema,
  subjectId: uuidSchema,
  staffId: uuidSchema,
  roomId: uuidSchema.optional()
});
var recordSubstitutionSchema = z.object({
  slotId: uuidSchema,
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use the format YYYY-MM-DD"),
  coveringStaffId: uuidSchema,
  reason: z.string().trim().max(240).optional()
});

export { ALL_MODULE_KEYS, CANDIDATE_LEVELS, CLASS_LEVELS, CLASS_LEVEL_GROUPS, CLASS_LEVEL_GROUP_LABELS, CLASS_LEVEL_LABELS, CURRENCIES, CurrencyMismatchError, DAY_LABELS, DEFAULT_CURRENCY, DEFAULT_NUMBER_FORMAT, FINANCIAL_ROLES, GENDERS, InvalidPhoneNumberError, LEVEL_GROUP, MODULE_KEYS, MODULE_LABELS, ORG_LEVEL_ROLES, PORTALS, RELATIONSHIPS, REQUIRED_STUDENT_FIELDS, RESIDENCES, ROLES, ROLE_LABELS, ROLE_PORTAL, SCHOOL_DAYS, SEGMENT_LIMITS, TERMS_PER_YEAR, TERM_LABELS, TERM_NUMBERS, TERM_NUMBER_VALUES, UG_COUNTRY_CODE, add, aggregationConfigSchema, allocate, arrearsQuerySchema, assessmentKindSchema, assignStaffSchema, attendanceStatusSchema, audienceSchema, calculateSmsCost, campaignSegments, canReceiveSms, canRender, changePasswordSchema, checkSubjectQuota, checkTeacherLoad, checkTeacherQualified, classLevelGroupSchema, classLevelSchema, coerceClassLevel, coerceDate, coerceGender, coerceRelationship, coerceResidence, coerceText, compare, computeOverallResult, computePositions, computeSubjectResult, computeTotalScore, confirmPasswordResetSchema, createAcademicYearSchema, createAdjustmentSchema, createAssessmentSchema, createCampaignSchema, createDiscountSchema, createFeeItemSchema, createFeeStructureSchema, createGradingSchemeSchema, createOrganizationSchema, createPeriodStructureSchema, createRoomSchema, createSchoolSchema, createStaffSchema, createStreamSchema, createStudentSchema, createSubjectSchema, createTemplateSchema, createTenantSchema, createTermSchema, createTimetableVersionSchema, dateOnlySchema, deriveShortCode, discountKindSchema, emailSchema, enrolStudentSchema, exitStudentSchema, extractPlaceholders, feeItemCategorySchema, findFreeStaff, findHardConflicts, findNonGsm7, formatIdentifier, formatMoney, formatUgPhone, genderSchema, generateInvoicesSchema, gradeBandInputSchema, guardianInputSchema, hasHardConflict, hasUsableName, isCandidateLevel, isGsm7, isNegative, isOrgLevelRole, isPositive, isValidUgPhone, isWithinQuietHours, isZero, levelGroupsSchema, levelOrder, levelsInGroup, linkGuardianSchema, loginSchema, lookupDivision, lookupGrade, majorToMinor, mapStudentHeaders, markAttendanceSchema, messageCategorySchema, minorToMajor, money, moneyStringSchema, negate, nextLevel, nextQuietHoursWindow, normalizeHeader, normalizeUgPhone, paginationSchema, passwordSchema, paymentMethodSchema, percentage, phoneKind, phoneSchema, placeSlotSchema, portalsForRoles, previewCampaignCost, primaryPortal, promoteSchema, qualificationKey, recordPaymentSchema, recordSubstitutionSchema, redeemPasswordResetSchema, refundCreditsSchema, relationshipSchema, renderTemplate, requestPasswordResetSchema, requiresTwoFactor, residenceSchema, resolveRecipients, reversePaymentSchema, sanitizeToGsm7, saveMarksSchema, setReportCardCommentsSchema, slugSchema, splitFullName, staffAccessRoleSchema, studentCategorySchema, studentQuerySchema, subtract, sum, topUpCreditsSchema, transferStreamSchema, tryNormalizeUgPhone, updateModulesSchema, updateOrganizationSchema, updatePlatformSettingsSchema, updateSchoolSchema, updateStaffAccessSchema, updateStaffSchema, updateStudentSchema, uuidSchema, validateDivisionBands, validateGradeBands, validateNumberFormat, voidInvoiceSchema, weeklyPeriodCount, weightedAverage, zero };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map